import ResponsiveTable from '../../components/ResponsiveTable';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOnChange, useUrlState } from '../../hooks/useUrlState';
import api from '../../utils/api';
import Swal from 'sweetalert2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faEdit, faCalendarAlt, faSearch, faSun, faMoon, faEye } from '@fortawesome/free-solid-svg-icons';
import Pagination from '../../components/Pagination'; 
import EmployeeAvatar from '../../components/EmployeeAvatar';
import FilterScroller from '../../components/FilterScroller';
import InactiveTag from '../../components/InactiveTag';
import { useRecentSearches } from '../../components/RecentSearches';
import '../../styles/App.css';

// The directory's status filter. Active is the default; Admin and HR can switch.
const STATUS_FILTERS = [
    { key: 'ACTIVE', label: 'Active' },
    { key: 'INACTIVE', label: 'Inactive' },
    { key: 'ALL', label: 'All' }
];

const Employees = () => {
    const navigate = useNavigate();
    const currentUser = JSON.parse(localStorage.getItem('user'));
    const userRole = currentUser?.role || 'EMPLOYEE';

    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useUrlState('page', 1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    // The address holds the search once typing pauses; the box starts from it.
    const [debouncedSearch, setDebouncedSearch] = useUrlState('q', '');
    const [searchTerm, setSearchTerm] = useState(debouncedSearch);
    const recentSearch = useRecentSearches('employees', { value: searchTerm, onPick: setSearchTerm });

    // Inactive employees are left out of the directory and its count. Admin and
    // HR can switch to them, to look someone up or reactivate them.
    const canSeeInactive = ['ADMIN', 'HR'].includes(userRole);
    const [statusFilter, setStatusFilter] = useUrlState('status', 'ACTIVE');

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(searchTerm), 500);
        return () => clearTimeout(timer);
    }, [searchTerm, setDebouncedSearch]);

    useOnChange(() => setCurrentPage(1), [debouncedSearch, statusFilter]);

    useEffect(() => {
        fetchEmployees(currentPage);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, itemsPerPage, debouncedSearch, statusFilter]);

    const fetchEmployees = async (pageToFetch) => {
        setLoading(true);
        try {
            const params = {
                page: pageToFetch,
                limit: itemsPerPage,
                search: debouncedSearch,
                status: statusFilter
            };

            const res = await api.get('/employees', { params });
            
            setEmployees(res.data.data);
            setTotalPages(res.data.pagination.totalPages);
            setTotalRecords(res.data.pagination.totalRecords);
            setCurrentPage(res.data.pagination.currentPage);
        } catch (err) {
            console.error(err);
            Swal.fire('Error', 'Failed to fetch employees', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleViewProfile = (id) => { recentSearch.save(); navigate(`/employee/${id}`); };
    const handleEditEmployee = (id) => navigate(`/edit-employee/${id}`);

    return (
        <div className="employee-page fade-in">
            <h1 className="page-title">
                {userRole === 'MANAGER' ? 'My Team Directory' : 'Employee Directory'}
            </h1>

            {/*
              * One toolbar: search, the status filter (Admin/HR) and Add
              * Employee on a single line, stacking on a phone.
              *
              * The search box has its own class rather than swal2-input: that
              * class carries !important width, height and margins meant for
              * popups, which no inline style can beat, and it is what pushed
              * these controls onto separate lines.
              */}
            <div className="filter-bar-card employee-toolbar">
                <div className="search-wrapper employee-search">
                    <FontAwesomeIcon icon={faSearch} className="search-icon" />
                    <input
                        {...recentSearch.inputProps}
                        type="text"
                        placeholder="Search name, ID or email..."
                        className="employee-search-input"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        aria-label="Search employees"
                    />
                    {recentSearch.menu}
                </div>

                {canSeeInactive && (
                    <FilterScroller role="group" ariaLabel="Employee status">
                        {STATUS_FILTERS.map(f => (
                            <button
                                key={f.key}
                                type="button"
                                className={`gts-btn filter-btn ${statusFilter === f.key ? 'primary active' : 'warning inactive'}`}
                                onClick={() => setStatusFilter(f.key)}
                                aria-pressed={statusFilter === f.key}
                            >
                                {f.label}
                            </button>
                        ))}
                    </FilterScroller>
                )}

                {userRole === 'ADMIN' && (
                    <button className="action-btn-primary employee-add-btn" onClick={() => navigate('/add-employee')}>
                        <FontAwesomeIcon icon={faPlus} /> Add Employee
                    </button>
                )}
            </div>

            <div className="employee-table-container fade-in">
                <ResponsiveTable className="employee-table">
                    <thead>
                        <tr>
                            <th>Employee</th>
                            <th>Joining Date</th>
                            <th>Role / Shift</th>
                            <th>Status</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr>
                                <td colSpan="5" className="empty-table-message">
                                    Loading employee records...
                                </td>
                            </tr>
                        ) : employees.length === 0 ? (
                            <tr>
                                <td colSpan="5" className="empty-table-message">
                                    No employees found matching your search.
                                </td>
                            </tr>
                        ) : (
                            employees.map(emp => (
                                <tr key={emp._id}>
                                    <td data-label="Employee">
                                        <div className="flex-row gap-10">
                                            <EmployeeAvatar person={emp} />
                                            <div>
                                                <div 
                                                    className="fw-600 text-dark-blue" 
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => handleViewProfile(emp._id)}
                                                >
                                                    {emp.name}
                                                    <InactiveTag person={emp} />
                                                </div>
                                                <div className="text-small text-muted">
                                                    <span className="fw-bold text-primary">{emp.employeeId || 'No ID'}</span> • {emp.email}
                                                </div>
                                            </div>
                                        </div>
                                    </td>

                                    <td data-label="Joining Date">
                                        <div className="fs-14 text-dark-gray">
                                            <FontAwesomeIcon icon={faCalendarAlt} className="text-muted mr-5" />
                                            {emp.joiningDate ? new Date(emp.joiningDate).toLocaleDateString('en-GB') : 'N/A'}
                                        </div>
                                    </td>

                                    <td data-label="Role / Shift">
                                        <div className="flex-col align-start gap-5">
                                            <span className={`role-tag ${emp.role.toLowerCase()}`}>
                                                {emp.role}
                                            </span>
                                            <span className="shift-badge">
                                                {emp.shiftType === 'NIGHT' ? (
                                                    <><FontAwesomeIcon icon={faMoon} className="text-moon mr-5" /> Night Shift</>
                                                ) : (
                                                    <><FontAwesomeIcon icon={faSun} className="text-sun mr-5" /> Day Shift</>
                                                )}
                                            </span>
                                        </div>
                                    </td>

                                    <td data-label="Status">
                                        <span className={emp.status === 'ACTIVE' ? 'status-active' : 'status-inactive'}>
                                            {emp.status}
                                        </span>
                                    </td>

                                    <td data-label="Actions">
                                        <div className="flex-row gap-5">
                                            <button 
                                                className="gts-btn doc-btn" 
                                                style={{ padding: '6px 10px', background: 'light-dark(#f1f5f9, var(--theme-surface-raised))', color: 'light-dark(#215D7B, var(--theme-accent-ink))' }}
                                                onClick={() => handleViewProfile(emp._id)}
                                                title="View Profile"
                                            >
                                                <FontAwesomeIcon icon={faEye} /> View
                                            </button>
                                            <button 
                                                className="edit-btn" 
                                                onClick={() => handleEditEmployee(emp._id)}
                                                title="Edit Settings"
                                            >
                                                <FontAwesomeIcon icon={faEdit} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </ResponsiveTable>
            </div>

            {!loading && (
                <Pagination 
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalRecords={totalRecords}
                    limit={itemsPerPage}
                    onPageChange={(page) => setCurrentPage(page)}
                    onLimitChange={(newLimit) => {
                        setItemsPerPage(newLimit);
                        setCurrentPage(1);
                    }}
                />
            )}
        </div>
    );
};

export default Employees;
